import "react-calendar/dist/Calendar.css";
import "./globals.scss";
import "./redesign.scss";
import "react-dropdown-tree-select/dist/styles.css";
import "dayjs/locale/en-gb";
import "@aws-amplify/ui-react/styles.css";

import { AppProps } from "next/app";
import { useCallback, useEffect, useRef, useState, Dispatch, SetStateAction } from "react";

import { setLogosTheme } from "@/lib/logos";
import { useIdle } from "react-use";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { isUndefined } from "lodash";
import axios from "axios";
import { installGetDeduplication } from "@/lib/dedupe-requests";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isToday from "dayjs/plugin/isToday";
import duration from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import isYesterday from "dayjs/plugin/isYesterday";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekday from "dayjs/plugin/weekday";
import { Context } from "@/lib/Context";
import Head from "next/head";
import RootLayout from "@/layouts/root/RootLayout";
import AuthLayout from "@/layouts/auth/AuthLayout";
import BrandLoading from "@/components/brand-loading/BrandLoading";
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { withApp } from "@/lib/app-hoc";
import Script from "next/script";
import SubscriptionPlans from "@/components/subscription-plans/SubscriptionPlans";
import {
    backgroundColorState,
    customViewsCurrentViewDataState,
    customViewsState,
    customViewsUnsavedState,
    darkerBackgroundColorState,
    darkSurfaceDefaultState,
    greyLighterState,
    greyMediumState,
    negativeState,
    neutralState,
    newbackgroundColorState,
    notificationsListState,
    ortexColorState,
    positiveState,
    stockTradingItemIdState,
    subscriptionOfferingsState,
    subscriptionState,
    textColorState,
    themeState,
    universesState,
    universeState,
    userState,
    whiteState,
    userDataState,
    showAppSubscriberModalState,
    showAgreementsModalState,
    greyState,
    chatModalState,
    contrastState,
    HIGH_CONTRAST_COLORS,
    moduleSettingsState,
    userColorSettingsState,
    isManualColor,
    UserColorKey,
    lighterOrtexColorState,
    blackMediumState,
    darkDefaultState,
    iconColorState,
    dividerDarkState,
    borderDarkState,
    popoverContentState,
    paymentModalOpenState,
} from "@/lib/store";
import { ThemeProvider } from "@mui/material/styles";
import { Backdrop, CircularProgress, CssBaseline, StyledEngineProvider } from "@mui/material";
import Payment from "@/components/payment/Payment";
import { User } from "@/models/auth";
import CustomViewsUnsavedChangesModal from "@/components/custom-views-unsaved-changes-modal/CustomViewsUnsavedChangesModal";
import { saveCustomViews } from "@/controllers/custom-views";
import { Universe } from "@/models/universe";
import { detectBot, detectDeviceType, isOrtexApp } from "@/lib/utils";
import { LicenseInfo } from "@mui/x-license-pro";
import { SocialLogin } from "@capgo/capacitor-social-login";
import AppSubscriberModal from "@/components/app-subscriber-modal";
import { signOutFromBackend } from "@/lib/signOutFromBackend";
import awsExports from "@/lib/aws-config";
import { Amplify } from "aws-amplify";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import useGetUserData from "src/hooks/useGetUserData";
import { saveCheckoutIntent, loadCheckoutIntent, clearCheckoutIntent } from "@/lib/checkoutIntent";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import AgreementsModal from "@/components/agreements-modal/AgreementsModal";
import CancellationLossWarning from "@/components/cancellation-loss-warning/CancellationLossWarning";
import GlobalTourLauncher from "@/components/tour/GlobalTourLauncher";
import SecFilingModal from "@/components/sec-filing-modal/SecFilingModal";
import NewsStoryModal from "@/components/news-story-modal/NewsStoryModal";
import { CallDetailsHost } from "@/components/call-details-modal/CallDetailsModal";
import SnackBar from "@/components/snack-bar/SnackBar";
import { getThemes } from "src/theme";
import { CONTRAST_SETTING_KEY, persistContrastSetting, readStoredContrast, storeContrast } from "@/lib/contrast";
import { useAppWebSockets } from "src/hooks/useAppWebSockets";
import { usePaymentConfig } from "src/hooks/usePaymentConfig";
import { usePushNotifications } from "src/hooks/usePushNotifications";
import { useAndroidBackButton } from "src/hooks/useAndroidBackButton";
import PushPrePrompt from "@/components/push-notifications/PushPrePrompt";
import AppUpdateBanner from "@/components/app-update/AppUpdateBanner";
import AppUrlListener from "@/components/app-url-listener/AppUrlListener";
import { useNotificationsFetcher } from "src/hooks/useNotificationsFetcher";
import PlanPickerDrawer from "@/components/plan-picker-drawer/PlanPickerDrawer";
import AltDataUpgradeDialog from "@/components/alt-data/AltDataUpgradeDialog";
import TrialFullAccessNotice from "@/components/trial-full-access-notice/TrialFullAccessNotice";
import { setTrackingUserId, trackRouteView, trackSignUp } from "@/lib/tracking";

dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(isYesterday);
dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(timezone);
dayjs.extend(weekday);

dayjs.locale("en-gb");

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_LICENSE_KEY);

const ReactTooltip = dynamic(() => import("react-tooltip"), { ssr: false });
// Chat opens from a menu action, so the modal and the ortex-charts packages it renders load on first open instead of in the shared _app bundle.
const ChatModal = dynamic(() => import("@/components/chat-modal/ChatModal"), {
    ssr: false,
    // Feedback while the chat chunk downloads on first open; fast loads (under 200 ms) show nothing, and a failed load
    // (offline, or a stale deploy) shows nothing rather than a backdrop that cannot be dismissed.
    loading: ({ pastDelay, error }) =>
        pastDelay && !error ? (
            <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        ) : null,
});

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";
axios.defaults.validateStatus = (status) => status < 500;
axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL;
axios.defaults.withCredentials = !!process.env.NEXT_PUBLIC_VERCEL_ENV;
axios.defaults.params = { GUIv: 2, authid: process.env.NEXT_PUBLIC_VERCEL_ENV ? undefined : "ortex" };

// Several pages mount a component more than once and fire the same GET two or three times
// within a few milliseconds. They overlap, so no HTTP cache collapses them and the backend
// does the work twice. This shares one round trip between concurrent identical GETs; see
// the module for what it deliberately does not do.
installGetDeduplication();

Amplify.configure(awsExports);

const FOUR_HOURS = 14400000;

// The sign-in / create-account pages render their form at once instead of under the full-screen
// loader. The loader waited for the user + universes requests (2-3 s), which a signed-out visitor
// does not need; a visitor who already holds a session still gets it while AuthPage forwards them.
const AUTH_FORM_ROUTES = ["/login", "/register"];

const hasStoredCognitoSession = (): boolean => {
    try {
        return Object.keys(localStorage).some((key) => key.startsWith("CognitoIdentityServiceProvider") && key.endsWith(".accessToken")) || (isOrtexApp() && !!localStorage.getItem("ortexUser"));
    } catch {
        return false;
    }
};

const PUBLIC_ROUTES = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes/", "/auth-redirect", "/apis", "/apis/plans", "/apis/credits", "/c/[slug]"];

const openEnrolment = (user: User, pathname: string, setter: Dispatch<SetStateAction<boolean>>) => {
    if (PUBLIC_ROUTES.includes(pathname)) setter(false);
    else {
        const deviceType = process.env.NEXT_PUBLIC_VERCEL_ENV ? detectDeviceType() : "Desktop";
        /* if (deviceType === "Mobile") setter(true); */
    }
};

// Per-session marker so the persisted checkout intent (src/lib/checkoutIntent.ts)
// is re-applied at most once per session instead of on every navigation.
const CHECKOUT_SHOWN_KEY = "ortex_checkout_shown";

// Plans that mean "already paying". Registered free users are "ORTEX Free" and
// anonymous visitors "ORTEX Visitor" — both should still be routed to checkout
// by marketing links, so guards must match the PAID names, not exclude
// "Visitor" (that substring check silently blocked every free user).
const PAID_PLAN_NAMES = ["ORTEX Basic", "ORTEX Advanced", "ORTEX Institutional"];
const isPaidPlan = (plan: string | null | undefined): boolean => !!plan && PAID_PLAN_NAMES.some((p) => plan.includes(p));

const App = ({ Component, pageProps, router: { events, pathname, query } }: AppProps) => {
    const [theme, setTheme] = useRecoilState(themeState);
    const [contrast, setContrast] = useRecoilState(contrastState);
    const [moduleSettings, setModuleSettings] = useRecoilState(moduleSettingsState);
    const userColorSettings = useRecoilValue(userColorSettingsState);
    // Colour atoms as they were before high contrast was applied (user settings
    // or defaults), so switching it off restores them exactly.
    const contrastBaseRef = useRef<Record<string, string> | null>(null);
    const contrastAppliedRef = useRef(false);
    const contrastUserRef = useRef<unknown>(undefined);
    const CONTRAST_ATOMS = {
        ortexColor: ortexColorState,
        lighterOrtexColor: lighterOrtexColorState,
        positive: positiveState,
        negative: negativeState,
        grey: greyState,
        blackMedium: blackMediumState,
        darkDefault: darkDefaultState,
        darkSurfaceDefault: darkSurfaceDefaultState,
        iconColor: iconColorState,
        dividerDark: dividerDarkState,
        borderDark: borderDarkState,
        popoverContent: popoverContentState,
    } as const;
    const readContrastColors = useRecoilCallback(
        ({ snapshot }) =>
            (): Record<string, string> => {
                const out: Record<string, string> = {};
                for (const [k, a] of Object.entries(CONTRAST_ATOMS)) out[k] = snapshot.getLoadable(a).getValue();
                return out;
            },
        []
    );
    const writeContrastColors = useRecoilCallback(
        ({ set }) =>
            (c: Record<string, string>) => {
                for (const [k, a] of Object.entries(CONTRAST_ATOMS)) if (c[k]) set(a, c[k]);
            },
        []
    );
    // Logo URLs carry the theme so the logo service serves artwork that reads on it.
    useEffect(() => {
        setLogosTheme(theme);
    }, [theme]);
    const userData = useRecoilValue(userDataState);
    const userDataRef = useRef(userData);
    const prevUserIdRef = useRef<number | undefined>(undefined);
    const pathnameRef = useRef(pathname);
    const ortexColor = useRecoilValue(ortexColorState);
    const positive = useRecoilValue(positiveState);
    const negative = useRecoilValue(negativeState);
    const grey = useRecoilValue(greyState);
    const neutral = useRecoilValue(neutralState);
    const textColor = useRecoilValue(textColorState);
    const greyMedium = useRecoilValue(greyMediumState);
    const greyLighter = useRecoilValue(greyLighterState);
    const backgroundColor = useRecoilValue(backgroundColorState);
    const darkerBackgroundColor = useRecoilValue(darkerBackgroundColorState);
    const darkSurfaceDefaultColor = useRecoilValue(darkSurfaceDefaultState);
    const newbackgroundColor = useRecoilValue(newbackgroundColorState);
    const universes = useRecoilValue(universesState);
    const whiteColor = useRecoilValue(whiteState);
    const setUniverse = useSetRecoilState(universeState);
    const user = useRecoilValue(userState);
    const [customViews, setCustomViews] = useRecoilState(customViewsState);
    const [unsavedChanges, setUnsavedChanges] = useRecoilState(customViewsUnsavedState);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const currentViewData = useRecoilValue(customViewsCurrentViewDataState);
    const [urlToGo, setUrlToGo] = useState(null);

    const subscription = useRecoilValue(subscriptionState);
    const [loading, setLoading] = useState(true);
    const [enrolment, setEnrolment] = useState<boolean>(null);
    const [offerings, setOfferings] = useRecoilState(subscriptionOfferingsState);

    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();

    const setNotificationsList = useSetRecoilState(notificationsListState);
    const stockTradingItemId = useRecoilValue(stockTradingItemIdState);

    const { asPath, push, replace } = useRouter();

    useAndroidBackButton();
    // undefined while the user loads, so the device is registered once with the right user.
    const pushAuthKey = isUndefined(userData) ? undefined : String(userData?.user_id ?? "anon");
    usePushNotifications(push, pushAuthKey);

    const [showAppSubscriberModal, setShowAppSubscriberModal] = useRecoilState(showAppSubscriberModalState);
    const [showAgreementsModal, setShowAgreementsModal] = useRecoilState(showAgreementsModalState);
    const [chatModal, setChatModal] = useRecoilState(chatModalState);
    const paymentModalOpen = useRecoilValue(paymentModalOpenState);

    const [promoCode, setPromoCode] = useState<string | null>(null);

    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const isAuthFormRoute = AUTH_FORM_ROUTES.includes(pathname);
    // Read after mount so the server HTML and the first client render agree (both: no session).
    const [hasLocalSession, setHasLocalSession] = useState(false);
    useEffect(() => {
        if (isAuthFormRoute) setHasLocalSession(hasStoredCognitoSession());
    }, [isAuthFormRoute]);

    // T&C suppression: while the payment modal is open, and for a couple of page
    // views after it closes, we hide the agreements popup so it can't cover the
    // checkout (or the win-back / discount modal that may appear right after the
    // user decides not to pay).
    const paymentModalOpenRef = useRef(false);
    const [tcCooldownViews, setTcCooldownViews] = useState(0);

    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const { light, dark } = getThemes({
        ortexColor,
        positive,
        negative,
        neutral,
        textColor,
        greyMedium,
        greyLighter,
        backgroundColor,
        darkerBackgroundColor,
        darkSurfaceDefaultColor,
        newbackgroundColor,
        whiteColor,
    });

    useEffect(() => {
        const initializeSocialLogin = async () => {
            await SocialLogin.initialize({
                google: {
                    webClientId: process.env.NEXT_PUBLIC_WEB_CLIENT_ID,
                    iOSClientId: process.env.NEXT_PUBLIC_IOS_CLIENT_ID,
                    iOSServerClientId: process.env.NEXT_PUBLIC_WEB_CLIENT_ID,
                },
            });
        };

        initializeSocialLogin();
    }, []);

    useEffect(() => {
        if (query.message === "welcome" && userData?.first_name) {
            setSnackBar({ open: true, text: `${userData.first_name}, welcome to ORTEX!`, severity: "success" });
        }
    }, [query.message, userData?.first_name]);

    // Keep refs in sync so Hub listener closures always see latest values
    useEffect(() => {
        userDataRef.current = userData;
    }, [userData]);

    useEffect(() => {
        pathnameRef.current = pathname;
    }, [pathname]);

    // Push user_id to dataLayer on sign-in / sign-out
    useEffect(() => {
        const userId = userData?.user_id;
        // Mirror it into the tracking module so events fired from plain click
        // handlers (outside any Recoil consumer) still carry the user.
        setTrackingUserId(userId ?? null);
        if (userId) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ user_id: userId });
        } else if (prevUserIdRef.current) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ user_id: null });
        }
        prevUserIdRef.current = userId;
    }, [userData?.user_id]);

    const amplifySignOut = async (vertexUser, ortexUser) => {
        try {
            await signOutFromBackend(vertexUser, ortexUser);
            await signOut();
        } catch (error) {
            console.error("Error signing out user", error);
        } finally {
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                    localStorage.removeItem(key);
                }
            });
            localStorage.removeItem("vertexUser");
            localStorage.removeItem("ortexUser");
        }
    };

    useEffect(() => {
        if (query.hasOwnProperty("promo")) {
            query.promo ? sessionStorage.setItem("promoCode", query.promo as string) : sessionStorage.setItem("promoCode", "true");

            setPromoCode(query.promo as string);
        }
    }, [query.message, query.promo]);

    // `?ai=open` deep-links the ORTEX AI chat modal. Used by other parts of
    // the product (e.g. the cancellation flow's "have you tried our AI
    // Assistant?" card) so a single click can drop the user straight into
    // the chat without making them hunt for the header button. We strip
    // the query param after opening so a refresh doesn't keep re-opening
    // the modal.
    useEffect(() => {
        if (query.ai === "open") {
            setChatModal({ open: true, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
            const { ai: _ai, ...rest } = query;
            replace({ pathname, query: rest }, undefined, { shallow: true });
        }
    }, [query.ai]);

    // Backward-compat: `?checkout=true` (or `?v=&t=` with a plan and
    // interval pre-selected) used to pop the payment modal when users
    // landed on /upgrade — that page does handleAppSubscriberUpgrade()
    // before redirecting to /. If a marketing link lands on a different
    // path (root, markets-overview, anywhere) the trigger never fired
    // and the user just sat on the page wondering why the checkout
    // didn't appear. This effect gives any path the same behaviour.
    //
    // Skips on the auth pages (the visitor signup flow has its own
    // payment trigger), when the modal is already open, and for
    // visitor / not-yet-loaded user state. Plain query.checkout reads
    // are guarded behind a `user` truthy check so we don't pre-fetch
    // subscription data for anonymous traffic.
    const wantsCheckoutFromUrl = query.checkout === "true" || (!!query.v && !!query.t);
    useEffect(() => {
        if (!wantsCheckoutFromUrl) return;
        if (!user || !user.subscription_plan) return;
        if (paymentModalOpen) return;
        if (subscription?.plans?.length) return;
        const skipPaths = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect", "/upgrade"];
        if (skipPaths.includes(pathname)) return;
        // A PAYING subscriber should never be pushed into checkout by a
        // marketing link — a TRYAI/teaser URL just lands them in the app.
        // NOTE: registered free users have subscription_plan "ORTEX Free"
        // (the "Visitor" plan is only the anonymous fallback), so the guard
        // must check the paid plan names explicitly — a "Visitor" substring
        // check blocks the entire free-user audience of the link.
        if (isPaidPlan(user.subscription_plan)) {
            clearCheckoutIntent();
            return;
        }
        // Anonymous visitors can't check out (the subscription endpoint has no
        // Stripe customer to bill and returns a plan-less payload, which used to
        // crash the payment modal). Send them to create an account instead, with
        // the checkout intent saved — after signup the intent-replay effect below
        // re-applies promo/plan and pops checkout automatically.
        if (!user.email || user.email === "visitor@ortex.com") {
            saveCheckoutIntent({ promo: typeof query.promo === "string" && query.promo !== "true" ? query.promo : undefined, t: query.t as string, v: query.v as string });
            replace("/register");
            return;
        }
        // Open modal + load subscription in the same call as /upgrade does.
        handleAppSubscriberUpgrade({ openPaymentModal: true });
    }, [wantsCheckoutFromUrl, user?.subscription_plan, user?.email, pathname, paymentModalOpen, subscription?.plans?.length]);

    // Persistent checkout intent (belt-and-braces — see src/lib/checkoutIntent.ts).
    //
    // Affiliate / marketing links carry `?checkout=true&promo=…`, but the social
    // OAuth round-trip + multi-hop login forwards (login → upgrade → home) strip
    // the query before the user lands, so the URL-driven trigger above never
    // fires. We stash the intent (localStorage + sessionStorage + cookie) the
    // moment we see `checkout=true` — the login page also saves it BEFORE its
    // social redirect — then re-apply it to the URL once the user lands
    // authenticated on a normal page. Covers "show checkout after a new signup"
    // and "show it again next login until they pay". The intent persists across
    // sessions (cleared once they have a paid plan); a per-session marker stops
    // it nagging on every navigation.
    useEffect(() => {
        if (query.checkout !== "true") return;
        saveCheckoutIntent({ promo: query.promo as string, t: query.t as string, v: query.v as string });
    }, [query.checkout, query.promo, query.t, query.v]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const intent = loadCheckoutIntent();
        if (!intent) return;
        if (!user || !user.subscription_plan) return;
        // Only for an authenticated (registered / logged-in) user — never pop
        // checkout at an anonymous visitor who merely touched an affiliate link.
        if (!user.email || user.email === "visitor@ortex.com") return;
        const skipPaths = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect", "/upgrade"];
        if (skipPaths.includes(pathname)) return;
        // Guardrail: a PAID subscriber should never be pushed into checkout.
        // Free users ("ORTEX Free" — not "Visitor", which is the anonymous
        // fallback plan) SHOULD get the replay: that's the whole point of
        // persisting the intent through signup.
        if (isPaidPlan(user.subscription_plan)) {
            clearCheckoutIntent();
            return;
        }
        // Already shown this session, or the URL already carries checkout → let
        // the URL-driven effect above handle it.
        if (sessionStorage.getItem(CHECKOUT_SHOWN_KEY) === "1") return;
        if (query.checkout === "true") return;
        sessionStorage.setItem(CHECKOUT_SHOWN_KEY, "1");
        const params = new URLSearchParams();
        if (intent.promo) params.set("promo", intent.promo);
        if (intent.t) params.set("t", intent.t);
        if (intent.v) params.set("v", intent.v);
        params.set("checkout", "true");
        replace(`${pathname}?${params.toString()}`, undefined, { shallow: true });
    }, [user?.subscription_plan, pathname, query.checkout]);

    // Robustly honor the affiliate post-login destination (ortex_post_login). The
    // signedIn Hub handler consumes it inside saveAuthTokens().then(), which
    // silently misses whenever saveAuthTokens rejects, no fresh signedIn event
    // fires (an existing Cognito session is reused), or it loses a redirect race —
    // leaving the user on the home page with the destination unconsumed. This
    // re-checks it on every authenticated landing, so the upgrade/checkout flow
    // still happens. ortex_post_login is only ever set by the affiliate social
    // deep-link, so this is a no-op for every normal login.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!user || !user.email || user.email === "visitor@ortex.com") return;
        const skipPaths = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect", "/upgrade"];
        if (skipPaths.includes(pathname)) return;
        const dest = sessionStorage.getItem("ortex_post_login");
        if (!dest) return;
        sessionStorage.removeItem("ortex_post_login");
        // Absolute URLs (e.g. an MCP connect return to ortex.com) need a real
        // cross-origin navigation; internal paths use the router. Only ever follow
        // an absolute URL that is ORTEX-owned (prevents open-redirect abuse).
        if (/^https?:\/\//i.test(dest)) {
            try {
                const u = new URL(dest);
                if (u.hostname === "ortex.com" || u.hostname.endsWith(".ortex.com")) {
                    window.location.href = dest;
                }
            } catch {
                /* ignore malformed */
            }
        } else {
            void replace(dest);
        }
    }, [user?.email, pathname]);

    // Track payment-modal open/close to drive the T&C cooldown (see state above).
    useEffect(() => {
        if (!paymentModalOpen && paymentModalOpenRef.current) {
            setTcCooldownViews(2);
        }
        paymentModalOpenRef.current = paymentModalOpen;
    }, [paymentModalOpen]);

    useEffect(() => {
        if (paymentModalOpen) return;
        setTcCooldownViews((n) => (n > 0 ? n - 1 : 0));
    }, [pathname]);

    const suppressTandcPopup = paymentModalOpen || tcCooldownViews > 0;

    const saveAuthTokens = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens?.idToken?.toString();

            if (!idToken) {
                throw new Error("No idToken in session");
            }

            // const vertexUrl = `${process.env.NEXT_PUBLIC_API_URL_VERTEX}/users/auth/cognito/`;
            const ortexUrl = `${process.env.NEXT_PUBLIC_API_URL}/users/auth/cognito/`;

            // const vertexConfig = {
            //     withCredentials: true,
            //     xsrfCookieName: "vertex_csrftoken",
            //     headers: { "Content-Type": "application/json" },
            //     params: null,
            // };

            const ortexConfig = {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                },
                params: null,
            };

            const [ortexSettled] = await Promise.allSettled([axios.post(ortexUrl, idToken, ortexConfig)]);

            const failureReasons: string[] = [];

            // if (vertexSettled.status === "fulfilled") {
            //     const vertexUserId = vertexSettled.value?.data?.userId;
            //     if (vertexUserId) {
            //         localStorage.setItem("vertexUser", vertexUserId);
            //     } else {
            //         const reason = "Vertex: missing userId in response";
            //         console.error(reason, vertexSettled.value?.data);
            //         failureReasons.push(reason);
            //     }
            // } else {
            //     const reason = `Vertex: request failed (${vertexSettled.reason?.message ?? vertexSettled.reason})`;
            //     console.error(reason, vertexSettled.reason);
            //     failureReasons.push(reason);
            // }

            if (ortexSettled.status === "fulfilled") {
                const ortexUserId = ortexSettled.value?.data?.userId;
                if (ortexUserId) {
                    localStorage.setItem("ortexUser", ortexUserId);
                } else {
                    const reason = "Ortex: missing userId in response";
                    console.error(reason, ortexSettled.value?.data);
                    failureReasons.push(reason);
                }
            } else {
                const reason = `Ortex: request failed (${ortexSettled.reason?.message ?? ortexSettled.reason})`;
                console.error(reason, ortexSettled.reason);
                failureReasons.push(reason);
            }

            if (failureReasons.length > 0) {
                throw new Error(failureReasons.join("; "));
            }
        } catch (error) {
            console.error("Error saving auth tokens:", JSON.stringify(error));
            throw error;
        }
    };

    useGetUserData(isAuthenticated);

    //auth
    useEffect(() => {
        const hubListenerCancel = Hub.listen("auth", async (data) => {
            const { payload } = data;
            const vertexUser = localStorage.getItem("vertexUser");
            const ortexUser = localStorage.getItem("ortexUser");

            switch (payload.event) {
                case "signedIn":
                    saveAuthTokens()
                        .then(() => {
                            setIsAuthenticated(true);
                            if (["/login", "/login-legacy", "/cognito-login", "/register", "/auth-redirect"].includes(pathnameRef.current)) {
                                void replace("/");
                            }
                            if (sessionStorage.getItem("isNewSignup") === "true") {
                                sessionStorage.removeItem("isNewSignup");
                                // userData is usually not loaded yet this soon after sign-in;
                                // saveAuthTokens has just stored the backend user id though
                                // (it can also hold the literal "true" on some social paths).
                                const storedUserId = Number(localStorage.getItem("ortexUser"));
                                trackSignUp(userDataRef.current?.user_id ?? (Number.isInteger(storedUserId) && storedUserId > 0 ? storedUserId : null));
                                handleAppSubscriberUpgrade({ openPaymentModal: true });
                            }
                        })
                        .catch((error) => {
                            console.error("Error saving auth tokens:", error);
                            setSnackBar({
                                open: true,
                                text: `Sign-in error: ${error?.message ?? "unknown error"}. Please try again or contact support.`,
                                severity: "error",
                            });
                        });
                    break;
                case "signInWithRedirect_failure":
                    // Clear any stashed affiliate destination so an abandoned/failed
                    // social sign-in can't redirect a later normal login to /upgrade.
                    sessionStorage.removeItem("ortex_post_login");
                    amplifySignOut(vertexUser, ortexUser);

                    setSnackBar({ open: true, text: "Sign in failed. Please try again.", severity: "error" });
                    break;
                default:
                    break;
            }
        });

        return () => {
            hubListenerCancel();
        };
    }, [handleAppSubscriberUpgrade, replace]);

    //notifications
    useNotificationsFetcher(asPath, stockTradingItemId, setNotificationsList);

    //subscription config
    usePaymentConfig(user, offerings, setOfferings);

    const userIdle = useIdle(FOUR_HOURS);

    const { multiWebSocket, ortexWebSocket } = useAppWebSockets(userIdle);

    useEffect(() => {
        if (userIdle) push("/idle");
    }, [userIdle]);

    let Layout = RootLayout;

    if (["/login", "/login-legacy", "/cognito-login", "/iframes", "/register", "/auth-redirect"].includes(pathname)) Layout = AuthLayout;

    useEffect(() => {
        axios.interceptors.response.use(
            (res) => res,
            (err) => {
                const { config, message } = err.toJSON();
                // Sign-out failures stay silent. Matched on the path because the URL is absolute
                // when NEXT_PUBLIC_API_URL is set: there the logout's redirect chain ends on
                // app.ortex.com, which the browser blocks as cross-origin, so it always "fails".
                const isLogout = /\/logout\/?$/.test((config.url ?? "").split("?")[0]);
                if (message !== "canceled" && !isLogout) {
                    const status = err.response?.status;
                    const text =
                        status === 429
                            ? "Too many requests. You have been temporarily rate limited: please wait a moment and try again."
                            : `Service error${status ? ` (${status})` : ""}. Please try again later.${
                                  process.env.NEXT_PUBLIC_VERCEL_ENV
                                      ? ""
                                      : `
                        ${message} - ${config.method.toUpperCase()} at:
                        ${config.url}`
                              }`;
                    setSnackBar({ open: true, text, severity: "error" });
                }
                throw err;
            }
        );
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        setTheme(["dark", "light"].includes(savedTheme) ? (savedTheme as "dark" | "light") : "dark");
    }, []);

    const onRouteChangeStart = useCallback(
        (url) => {
            if (pathname.includes("/custom-views") && unsavedChanges) {
                setUrlToGo(url);
                setShowUnsavedChangesModal(true);
                throw new Error("Unsaved changes");
            } else {
                setLoading(true);
            }
        },
        [unsavedChanges]
    );

    useEffect(() => {
        events.on("routeChangeStart", onRouteChangeStart);

        return () => {
            events.off("routeChangeStart", onRouteChangeStart);
        };
    }, [onRouteChangeStart]);

    // First-party click-id capture (?oppref=) and OpenAI page_viewed, on load and on
    // every client-side navigation. See trackRouteView in src/lib/tracking.ts.
    useEffect(() => {
        trackRouteView();
        events.on("routeChangeComplete", trackRouteView);
        return () => {
            events.off("routeChangeComplete", trackRouteView);
        };
    }, [events]);

    useEffect(() => {
        const routeChangeStart = () => setLoading(true);
        const routeChangeError = () => setLoading(false);
        const routeChangeComplete = () => setLoading(false);

        events.on("routeChangeError", routeChangeError);
        events.on("routeChangeComplete", routeChangeComplete);

        return () => {
            events.off("routeChangeStart", routeChangeStart);
            events.off("routeChangeError", routeChangeError);
            events.off("routeChangeComplete", routeChangeComplete);
        };
    }, [user]);

    // Terms & Conditions consent modal — gated to NOT open while a payment
    // flow is active. Previously the consent dialog (MUI Dialog, z=1300)
    // popped on top of the payment Drawer (z=1200) for fresh signups,
    // forcing the user to agree before they could even see the payment
    // form. Paying for a Stripe subscription is itself a T&C acceptance
    // (the Stripe Checkout consent_collection block in
    // create_checkout_session_subscription enforces this), so deferring
    // our own modal until after payment doesn't open any legal exposure.
    //
    // Deps include both `paymentModalOpen` (set by our app code when a
    // user clicks Subscribe) AND `subscription?.plans?.length` (Payment
    // also renders when the subscription state has plans loaded — see
    // the Payment render condition above). When either becomes truthy
    // we suppress the agreements modal; when both clear (post-payment
    // success or user closes the drawer) the effect re-fires and the
    // modal opens for users whose T&C still hasn't been accepted.
    useEffect(() => {
        const paymentInProgress = paymentModalOpen || !!subscription?.plans?.length;
        if (paymentInProgress) {
            setShowAgreementsModal(false);
            return;
        }
        setShowAgreementsModal((user.ortex_tandc && user.ortex_tandc !== "agreed") || user?.subscription_plan?.includes("Visitor"));
    }, [user, paymentModalOpen, subscription?.plans?.length]);

    useEffect(() => {
        if (pathname.includes("/custom-views")) {
            const beforeUnload = (e: BeforeUnloadEvent) => {
                if (unsavedChanges) {
                    e.preventDefault();
                    e.returnValue = "";
                }
            };

            window.addEventListener("beforeunload", beforeUnload);

            return () => {
                window.removeEventListener("beforeunload", beforeUnload);
            };
        }
    }, [unsavedChanges]);

    const handleAgree = () => {
        // "Save and leave" has to actually persist. This used to only copy the
        // working data into recoil, which survived the navigation but not a
        // reload — the user was told their view was saved when it was not
        // (ortex-gui#1195).
        saveCustomViews(currentViewData).catch(() => undefined);
        setImmediate(() => {
            setUnsavedChanges(false);
            urlToGo && push(urlToGo);
        });
        setShowUnsavedChangesModal(false);
        setCustomViews(currentViewData);
        setUrlToGo(null);
    };

    const handleContinueWithoutSaving = () => {
        setImmediate(() => {
            urlToGo && push(urlToGo);
        });
        setUnsavedChanges(false);
        setShowUnsavedChangesModal(false);
        setUrlToGo(null);
    };

    const [flattenedUniverses, setFlattenedUniverses] = useState<Universe[]>([]);

    useEffect(() => {
        if (universes && universes.selected !== undefined) {
            const flattened = Object.values(universes?.regions)
                .reduce((acc, val) => acc.concat(val), [])
                .concat(universes.portfolios);

            setFlattenedUniverses(flattened);
        }
    }, [universes]);

    useEffect(() => {
        if (query.uni_id && flattenedUniverses.length) setUniverse(flattenedUniverses.find((universe) => universe.id === Number(query.uni_id)));
    }, [query, universes]);

    const { userLoading } = useGetUserData();

    useEffect(() => {
        setLoading(userLoading);

        if (user.overdue && (!isOrtexApp() || offerings)) {
            setSnackBar({ open: true, text: "Your last payment failed. Please update your card details to continue to use ORTEX. We will not charge you for previous overdue invoices.", severity: "error" });
        }
    }, [userLoading]);

    useEffect(() => {
        // Don't run while a Hosted-UI OAuth redirect is completing — Amplify keeps the
        // PKCE verifier / inflight flag in CognitoIdentityServiceProvider.* localStorage
        // keys, and wiping them here aborts the code-for-token exchange (stuck spinner).
        if (pathname === "/auth-redirect") return;
        // Nor straight after a sign-in in this tab. `user` still holds the visitor fetched on
        // /login until useGetUserData reloads it, while the sign-in has already moved the route
        // to "/": wiping here then deletes the tokens Amplify has just stored. ORTEX carries on
        // through its session cookie, but every Vertex request goes out without a token.
        if (isAuthenticated) return;
        if (user?.subscription_plan === "ORTEX Visitor") {
            Object.keys(localStorage).forEach((key) => {
                if (/\.(oauthPKCE|oauthState|inflightOAuth)$/.test(key)) return; // preserve in-flight OAuth
                if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                    localStorage.removeItem(key);
                }
            });
        }
    }, [user, pathname, isAuthenticated]);

    useEffect(() => openEnrolment(user, pathname, setEnrolment), [pathname, user]);

    // Expose the active theme on <html> so plain CSS (redesign.scss) can scope dark-mode rules.
    useEffect(() => {
        if (theme) document.documentElement.setAttribute("data-theme", theme);
    }, [theme]);

    // Display contrast (normal | high). First paint uses the browser mirror;
    // ?contrast=high|normal is treated like flipping the switch (persisted once
    // the user is known). The saved accessibility setting below is the source of truth.
    const contrastFromUrlRef = useRef<"normal" | "high" | null>(null);
    useEffect(() => {
        const fromUrl = new URLSearchParams(window.location.search).get("contrast");
        if (fromUrl === "high" || fromUrl === "normal") {
            contrastFromUrlRef.current = fromUrl;
            storeContrast(fromUrl);
        }
        setContrast(readStoredContrast());
    }, []);

    // Settings > Accessibility > High Contrast (module_settings) wins whenever it
    // is defined; keep the browser mirror in step. A URL choice made on this
    // load is written to the account once, then the setting takes over.
    useEffect(() => {
        if (!userData) return;
        const isVisitor = userData.subscription_plan === "ORTEX Visitor";
        if (contrastFromUrlRef.current) {
            const chosen = contrastFromUrlRef.current;
            contrastFromUrlRef.current = null;
            persistContrastSetting(chosen, isVisitor, setModuleSettings);
            setContrast(chosen);
            return;
        }
        const saved = moduleSettings?.[CONTRAST_SETTING_KEY]?.value;
        if (saved === undefined) return;
        const next = saved ? "high" : "normal";
        storeContrast(next);
        setContrast(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, moduleSettings]);

    // Mirror the preference on <html> (redesign.scss keys the CSS tokens off it)
    // and swap the Recoil colour atoms so charts and inline colours follow.
    // Re-runs when the user loads, because userState re-seeds the atoms from
    // the saved settings at that point.
    useEffect(() => {
        document.documentElement.setAttribute("data-contrast", contrast);
        const high = contrast === "high" && theme === "dark";
        const userChanged = contrastUserRef.current !== userData;
        contrastUserRef.current = userData;
        if (high) {
            if (!contrastAppliedRef.current || userChanged) contrastBaseRef.current = readContrastColors();
            // Manual colour choices win in every mode: only untouched colours
            // get the high-contrast values.
            const hc: Record<string, string> = { ...HIGH_CONTRAST_COLORS };
            (["positive", "negative", "grey", "blackMedium"] as UserColorKey[]).forEach((k) => {
                if (isManualColor(k, userColorSettings[k])) hc[k] = userColorSettings[k] as string;
            });
            writeContrastColors(hc);
            contrastAppliedRef.current = true;
        } else if (contrastAppliedRef.current) {
            if (contrastBaseRef.current) writeContrastColors(contrastBaseRef.current);
            contrastAppliedRef.current = false;
            contrastBaseRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contrast, theme, userData]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme === "dark" ? dark : light}>
                <CssBaseline />
                <Context.Provider value={{ multiWebSocket, ortexWebSocket }}>
                    <style jsx global>{`
                        ::-webkit-scrollbar-thumb {
                            border: 4px solid ${isUndefined(theme) ? "unset" : backgroundColor};
                            background-color: ${isUndefined(theme) ? "unset" : theme === "dark" ? greyMedium : greyLighter};
                        }

                        html {
                            color: ${isUndefined(theme) ? "unset" : textColor};
                            scrollbar-color: ${isUndefined(theme) ? "unset" : theme === "dark" ? greyMedium : greyLighter} transparent;
                        }
                        .positive {
                            color: ${positive};
                        }
                        .bg-positive {
                            background-color: ${positive};
                        }
                        .border-positive {
                            border: solid;
                            border-color: ${positive};
                        }
                        .negative {
                            color: ${negative};
                        }
                        .bg-negative {
                            background-color: ${negative};
                        }
                        .bc-negative {
                            border-color: ${negative};
                        }
                        .bc-positive {
                            border-color: ${positive};
                        }
                        .border-negative {
                            border: solid;
                            border-color: ${negative};
                        }
                        .grey {
                            color: ${grey};
                        }
                        .neutral {
                            color: ${neutral};
                        }
                        .bg-neutral {
                            background-color: ${neutral};
                        }
                        .border-neutral {
                            border: solid;
                            border-color: ${neutral};
                        }
                        form iframe {
                            margin: 0 !important;
                            width: 100% !important;
                        }
                        .MuiBackdrop-root {
                            background-color: rgba(0, 0, 0, 0.5);
                        }
                        .MuiDataGrid-paper {
                            background-color: ${theme === "dark" ? "rgb(48 67 77)" : backgroundColor};
                        }

                        .MuiDataGrid-menu .MuiPaper-root {
                            background-color: ${theme === "dark" ? "rgb(48 67 77)" : backgroundColor};
                        }

                        .MuiDateRangePickerViewDesktop-root {
                            background-color: ${theme === "dark" ? "rgb(48 67 77)" : backgroundColor};
                        }

                        .MuiDateRangePickerViewDesktop-root .MuiSvgIcon-root {
                            color: ${theme === "dark" ? "#FFF" : "#000"};
                        }
                        .MuiDateRangePickerViewDesktop-root .defaultCalendarMonth {
                            color: "green";
                        }

                        .MuiToggleButtonGroup-grouped {
                            border-color: ${theme === "dark" ? "rgba(230,230,230, 0.5)" : null};
                        }

                        .MuiDialog-paper {
                            @media screen and (min-width: 3840px) {
                                max-width: 1400px;
                            }
                        }
                    `}</style>
                    <Script src="https://www.googletagmanager.com/gtag/js?id=G-4PM7DXFDTE" strategy="afterInteractive" />
                    <Script id="google-analytics" strategy="afterInteractive">
                        {`
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){window.dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', 'G-4PM7DXFDTE');
                `}
                    </Script>
                    {isOrtexApp() && <AppUrlListener />}
                    <ReactTooltip type={theme === "dark" ? "light" : "dark"} className="ReactTooltip" />
                    {subscription && !PUBLIC_ROUTES.includes(pathname) && <SubscriptionPlans data={subscription} />}
                    {/* Overridable social defaults. next/head keeps the last tag per
                        name / key, so a page's own twitter:* or og:image (key="og:image")
                        replaces these instead of being emitted alongside them. */}
                    <Head>
                        {/* Search-engine + AI-overview hints. `max-snippet=-1` lets Google quote
                            the page in full; `max-image-preview=large` is the signal for rich-image
                            eligibility. Lives here (not _document) so a page's own robots meta
                            (key="robots", or any meta name="robots") replaces it. */}
                        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" key="robots" />
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:image" content="https://app.ortex.com/icons/ogimage.png" />
                        <meta property="og:image" content="https://app.ortex.com/icons/ogimage.png" key="og:image" />
                    </Head>
                    <Layout {...pageProps}>
                        {(isUndefined(userData) || loading) && !(isAuthFormRoute && !hasLocalSession) && <BrandLoading {...pageProps} pathname={pathname} />}
                        {(user.email !== "visitor1@ortex.com" || PUBLIC_ROUTES.includes(pathname)) && <Component {...pageProps} />}
                    </Layout>
                    {isOrtexApp() && <PlanPickerDrawer />}
                    <AltDataUpgradeDialog />
                    {!PUBLIC_ROUTES.includes(pathname) && <TrialFullAccessNotice />}
                    {(!!subscription?.plans?.length || paymentModalOpen) && !user.app_subscriber_only && !isOrtexApp() && <Payment hidden={!!(promoCode?.toUpperCase() === "FEMALEINVEST")} />}
                    <CustomViewsUnsavedChangesModal show={showUnsavedChangesModal} setShow={setShowUnsavedChangesModal} agree={handleAgree} continueWithoutSaving={handleContinueWithoutSaving} />
                    {/* {enrolment && !detectGoogleBot() && <MobileDrawer />} */}
                    {showAppSubscriberModal && subscription && !isOrtexApp() && <AppSubscriberModal open={showAppSubscriberModal} setOpen={setShowAppSubscriberModal} />}

                    {!detectBot() && user && showAgreementsModal && !suppressTandcPopup && !["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect"].includes(pathname) && (
                        <AgreementsModal open={showAgreementsModal} setOpen={setShowAgreementsModal} visitor={user?.subscription_plan?.includes("Visitor")} user={user} />
                    )}
                    {/* Cancellation loss warning — surfaces for users
                        whose subscription is cancelled and ends within 30
                        days. Self-fetches the cancellation context, decides
                        whether to show, and handles the abort call. Skips
                        on auth pages so we don't show it during signup /
                        login flows. */}
                    {!detectBot() && user && !["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect"].includes(pathname) && <CancellationLossWarning />}
                    {/* App-wide tour runner: ?tour=<id> deep links + startTour()
                        (the in-house Appcues replacement). */}
                    {!detectBot() && <GlobalTourLauncher />}
                    {isOrtexApp() && <PushPrePrompt pathname={pathname} ready={!loading && !isUndefined(userData)} />}
                    {isOrtexApp() && <AppUpdateBanner pathname={pathname} ready={!loading && !isUndefined(userData)} />}
                    <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
                    {/* In-app viewer for SEC filing links (opened via the
                        ortex:open-sec-doc window event from table cells). */}
                    {!detectBot() && <SecFilingModal />}
                    {/* The news story reader every news list opens; ?show=news&id= links to a story. */}
                    {!detectBot() && <NewsStoryModal />}
                    {!detectBot() && <CallDetailsHost />}
                    {chatModal.open && (
                        <ChatModal
                            open={chatModal.open}
                            onClose={() => setChatModal({ open: false, initialMessage: undefined, startingModel: undefined, chatModel: undefined })}
                            initialMessage={chatModal.initialMessage}
                            startingModel={chatModal.startingModel}
                            chatModel={chatModal.chatModel}
                        />
                    )}
                </Context.Provider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default withApp(App);
